/* eslint-disable prefer-promise-reject-errors */
import React, { useCallback, useEffect, useState } from 'react';
import { Input, Modal, Select, Form, App } from 'antd-v5';
import { connect } from 'react-redux';
import { MailOutlined, UserOutlined, IdcardOutlined, CalendarOutlined, KeyOutlined } from '@ant-design/icons';
import PhoneInput from '../../CustomizedAntDesign/PhoneInput';
import { notificationSuccess } from '../../../core/actions';
import { postRequest } from '../../../core/apiRequests';

const createAssociate = async ({ data, loadingStatusOff = false, axiosConfig = {}, merchantId }) => {
  const reqObj = {
    params: ['associates', merchantId, 'save'],
    data,
    loadingStatusOff,
    ...axiosConfig,
  };

  return await postRequest(reqObj);
};

const AddAssociateModal = ({
  open,
  setOpen,
  initialValues,
  auth,
  onSuccess,
  requiredFields = ['name', 'storeId', 'tempRole', 'email', 'pin', 'role'],
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const roles = ['Admin', 'Manager', 'Associate'];
  const [sendInvitation, setSendInvitation] = useState(true);

  const [modal, contextHolder] = Modal.useModal();

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
    return () => {
      form.resetFields();
    };
  }, [initialValues]);

  // Clear modal fields after save
  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open]);

  const handleOnCancel = useCallback(() => {
    form.resetFields();
    setOpen(false);
  }, [form]);

  const handleOnSave = useCallback(async () => {
    setLoading(true);
    try {
      const values = form.getFieldsValue();
      await form.validateFields();
      let store_name = [];
      const store = auth.stores.find(st => st.id === values?.storeId);
      if (store) {
        store_name = store?.name || '';
      }
      const data = {
        ...values,
        mobileNum: parsePhoneNumber(values.mobile),
        // store_name,
        conditionedStores: values.storeId,
        emailAddress: values.email,
        associateId: auth.userId,
        merchantId: auth.merchantId,
        userPin: values.pin,
        schedulingLink: values.schedulingLink,
        associatePOS: values.posId,
        sendInvite: sendInvitation,
      };
      const response = await createAssociate({
        data,
        loadingStatusOff: false,
        merchantId: auth.merchantId,
      });
      if (onSuccess) {
        onSuccess(response);
        setOpen(false);
      }
      notificationSuccess('Associate Created Successfully');
      setLoading(false);
      setOpen(false);
    } catch (err) {
      // try catch handles erroneous input
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <>
      {contextHolder}
      <Modal
        title="New Associate"
        open={open}
        onOk={handleOnSave}
        onCancel={handleOnCancel}
        okText="Save"
        centered
        width={425}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form labelCol={{ span: 8 }} style={{ width: '100%' }} autoComplete="off" form={form} name="add-associate-form">
          <Form.Item
            name="firstName"
            validateTrigger="onSubmit"
            rules={[
              {
                type: 'string',
                min: 2,
                message: 'Please Enter a Valid Name',
              },
              {
                required: requiredFields.includes('name'),
                message: 'First name is required',
              },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="First Name" disabled={loading} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="lastName"
            validateTrigger="onSubmit"
            rules={[
              {
                type: 'string',
                min: 2,
                message: 'Please Enter a Valid Name',
              },
              {
                required: requiredFields.includes('name'),
                message: 'Last name is required',
              },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Last Name" disabled={loading} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="mobile"
            validateTrigger="onSubmit"
            rules={[
              {
                required: requiredFields.includes('mobile'),
                message: 'Phone Number is required',
              },
              () => ({
                validator(rule, value) {
                  if (!value) {
                    return Promise.resolve();
                  }
                  if (value && ((!value?.phoneNumber && !value?.areaCode) || value?.valid())) {
                    return Promise.resolve();
                  }
                  return Promise.reject('The inserted phone number  is not valid !');
                },
              }),
            ]}
          >
            <PhoneInput
              style={{
                width: '100%',
                borderRadius: '4px',
              }}
              country="us"
              required={false}
              placeholder="Phone Number"
              disabled={loading}
            />
          </Form.Item>
          <Form.Item
            name="email"
            validateTrigger="onSubmit"
            rules={[
              {
                type: 'email',
                message: 'Please Enter a Valid Email',
              },
              {
                required: requiredFields.includes('email'),
                message: 'Email is required',
              },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Email" type="email" disabled={loading} />
          </Form.Item>
          {/* Role */}
          <Form.Item
            name="role"
            validateTrigger="onSubmit"
            rules={[
              {
                required: requiredFields.includes('role'),
                message: 'Role is required',
              },
            ]}
          >
            <Select
              style={{ width: '100%' }}
              options={roles.map(role => ({
                label: `Role: ${role}`,
                value: role.toUpperCase(),
              }))}
              disabled={loading}
              placeholder="Role"
              onChange={selectedRole => {
                if (selectedRole === 'ADMIN') {
                  const allStoreIds = auth.stores.filter(s => s.id !== 0).map(st => st.id);
                  form.setFieldsValue({ storeId: allStoreIds });
                }
              }}
            />
          </Form.Item>
          {/* Store(s) */}
          <Form.Item
            name="storeId"
            validateTrigger="onSubmit"
            rules={[
              {
                required: requiredFields.includes('storeId'),
                message: 'Store access required',
              },
            ]}
          >
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              options={[
                { label: 'Select All Stores', value: 'ALL' },
                ...auth.stores
                  .filter(s => s.id !== 0)
                  .map(st => ({
                    label: `Store Access: ${st.name}`,
                    value: st.id,
                  })),
              ]}
              disabled={loading}
              placeholder="Store Access"
              onChange={selected => {
                const allStoreIds = auth.stores.filter(s => s.id !== 0).map(st => st.id);
                // If all stores are already selected and 'ALL' is clicked, deselect all
                if (selected.includes('ALL') && selected.length === allStoreIds.length + 1) {
                  form.setFieldsValue({ storeId: [] });
                } else if (selected.includes('ALL')) {
                  form.setFieldsValue({ storeId: allStoreIds });
                } else {
                  form.setFieldsValue({ storeId: selected });
                }
              }}
              value={form.getFieldValue('storeId')}
            />
          </Form.Item>
          {/* POS ID */}
          <Form.Item
            name="posId"
            validateTrigger="onSubmit"
            rules={[
              {
                type: 'string',
              },
            ]}
          >
            <Input prefix={<IdcardOutlined />} placeholder="Point of Sale ID" disabled={loading} autoComplete="off" />
          </Form.Item>
          {/* Scheduling Link */}
          <Form.Item
            name="schedulingLink"
            validateTrigger="onSubmit"
            rules={[
              {
                type: 'string',
              },
            ]}
          >
            <Input prefix={<CalendarOutlined />} placeholder="Scheduling link" disabled={loading} autoComplete="off" />
          </Form.Item>
          {/* Pin */}
          <Form.Item
            name="pin"
            validateTrigger="onSubmit"
            rules={[
              {
                type: 'string',
                min: 4,
                max: 4,
                message: 'Pin must be 4 digits in length',
                pattern: /^\d{4}$/, //format to accept on numbers
              },
            ]}
          >
            <Input
              prefix={<KeyOutlined />}
              placeholder="Pin: 4321"
              disabled={loading}
              autoComplete="off"
              minLength
              maxLength={4}
              onChange={e => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                form.setFieldsValue({ pin: value });
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});
export default connect(mapStateToProps, {})(AddAssociateModal);

function parsePhoneNumber(phoneNumber) {
  if (!phoneNumber) return null;
  if (typeof phoneNumber === 'string') {
    return phoneNumber.includes('+') ? phoneNumber : `+${phoneNumber}`;
  }
  return `+${phoneNumber.countryCode}${phoneNumber.areaCode}${phoneNumber.phoneNumber}`;
}
